/**
 * Service detail page.
 *
 * Renders complete, authored service content from src/content/services/
 * including candidacy, diagnosis, process steps, technology/materials,
 * FAQs, related links, and risk/limitation disclosures.
 *
 * Each section follows semantic HTML best practices (real sections, heading order,
 * lists as lists, tables as tables). RTL rendering uses logical utilities.
 * Demo/unverified content is visibly labelled per content provenance.
 *
 * NOTE: Google deprecated FAQPage markup 2026-05-07. FAQ content is retained
 * for users; the schema.org markup is deliberately omitted. Do not add it.
 */

import { useParams } from "react-router-dom";
import QuickActionBar from "@/components/QuickActionBar";
import Seo from "@/components/Seo";
import { useLanguage } from "@/contexts/LanguageContext";
import NotFound from "./NotFound";
import type { Locale } from "@/content/types";
import { isTrustworthy } from "@/content/types";
import { getService, PUBLISHED_SERVICE_SLUGS } from "@/content/services";
import { getDoctor, DOCTOR_SLUGS } from "@/content/doctors";
import { getCase, CASE_SLUGS } from "@/content/cases";
import { clinicNode, breadcrumbNode } from "@/lib/schema";
import { absoluteUrl } from "@/lib/seo";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import { motion } from "framer-motion";

interface ServicePageProps {
  locale: Locale;
}

/**
 * Render a localized badge for content whose provenance is demo or unverified.
 * This is required by the content contract to make it impossible to mistake
 * placeholder content for verified fact.
 */
function UnverifiedBadge({ locale }: { locale: Locale }) {
  const text =
    locale === "ar"
      ? "بيانات تجريبية — قيد انتظار تأكيد العيادة"
      : "Placeholder — pending clinic confirmation";
  return (
    <span className="inline-block px-3 py-1 me-3 mb-4 rounded-full text-sm font-medium bg-slate-700/60 text-slate-300 border border-slate-600">
      {text}
    </span>
  );
}

export default function ServicePage({ locale }: ServicePageProps) {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useLanguage();

  if (!slug) return <NotFound locale={locale} />;

  const content = getService(slug);

  // If this slug has no published content, render NotFound.
  // This prevents thin empty pages from being indexed.
  if (!content) return <NotFound locale={locale} />;

  const meta = content.meta[locale];
  const path = `/${locale}/services/${slug}`;

  // Breadcrumb: Home → Services → This Service
  const breadcrumb = breadcrumbNode(locale, [
    {
      name: locale === "ar" ? "الرئيسية" : "Home",
      url: absoluteUrl(`/${locale}`),
    },
    {
      name: locale === "ar" ? "الخدمات" : "Services",
      url: absoluteUrl(`/${locale}/services`),
    },
    {
      name: content.name[locale],
      url: absoluteUrl(path),
    },
  ]);

  // For schema, only emit clinic node + breadcrumb. Do NOT emit FAQPage
  // (deprecated 2026-05-07), AggregateRating, or Review (per guidelines).
  const schemas = [clinicNode(locale), breadcrumb];

  // Collect all related resources, filtering for published content only
  const relatedServicesList = content.relatedServices
    .filter((s) => PUBLISHED_SERVICE_SLUGS.includes(s))
    .map((s) => {
      const svc = getService(s);
      return svc ? { slug: s, name: svc.name[locale] } : null;
    })
    .filter(Boolean);

  const relatedDoctorsList = content.relatedDoctors
    .filter((d) => DOCTOR_SLUGS.includes(d))
    .map((d) => {
      const doc = getDoctor(d);
      return doc ? { slug: d, name: doc.name[locale] } : null;
    })
    .filter(Boolean);

  const relatedCasesList = content.relatedCases
    .filter((c) => CASE_SLUGS.includes(c))
    .map((c) => {
      const cas = getCase(c);
      return cas ? { slug: c, name: cas.title[locale] } : null;
    })
    .filter(Boolean);

  return (
    <>
      <Seo
        title={meta.title}
        description={meta.description}
        path={path}
        locale={locale}
        ogTitle={meta.ogTitle}
        ogDescription={meta.ogDescription}
        schema={schemas}
      />

      {/* Navbar and Footer are mounted once in src/components/Layout.tsx. */}

      <main className="min-h-screen">
        <div className="container mx-auto px-4 py-20 max-w-4xl">
          {/* ===== HERO: H1 + Summary ===== */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-20"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-8 text-white">
              {content.h1[locale]}
            </h1>
            <p className="text-xl text-slate-300 leading-relaxed mb-8">
              {content.summary[locale]}
            </p>
          </motion.section>

          {/* ===== CANDIDACY ===== */}
          <motion.section
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            className="mb-16"
          >
            <h2 className="text-3xl font-bold text-white mb-6">
              {locale === "ar" ? "من المرشحون لهذا العلاج" : "Who is a candidate"}
            </h2>
            <ul className="space-y-3">
              {content.candidacy[locale].map((item, i) => (
                <li key={i} className="flex gap-3 text-slate-300">
                  <span className="text-primary shrink-0 me-2">•</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </motion.section>

          {/* ===== PROBLEMS ADDRESSED ===== */}
          <motion.section
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            className="mb-16"
          >
            <h2 className="text-3xl font-bold text-white mb-6">
              {locale === "ar" ? "المشاكل التي يعالجها" : "Problems addressed"}
            </h2>
            <ul className="space-y-3">
              {content.problemsAddressed[locale].map((item, i) => (
                <li key={i} className="flex gap-3 text-slate-300">
                  <span className="text-primary shrink-0 me-2">•</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </motion.section>

          {/* ===== OPTIONS ===== */}
          <motion.section
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            className="mb-16"
          >
            <h2 className="text-3xl font-bold text-white mb-8">
              {locale === "ar" ? "الخيارات المتاحة" : "Treatment options"}
            </h2>
            <div className="space-y-8">
              {content.options.map((opt, i) => (
                <div
                  key={i}
                  className="p-6 rounded-lg bg-slate-900/40 border border-white/5"
                >
                  <h3 className="text-xl font-bold text-white mb-3">
                    {opt.name[locale]}
                  </h3>
                  <p className="text-slate-300 leading-relaxed">
                    {opt.body[locale]}
                  </p>
                </div>
              ))}
            </div>
          </motion.section>

          {/* ===== DIAGNOSIS ===== */}
          <motion.section
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            className="mb-16"
          >
            <h2 className="text-3xl font-bold text-white mb-6">
              {locale === "ar" ? "التشخيص" : "Diagnosis & planning"}
            </h2>
            <p className="text-slate-300 leading-relaxed">
              {content.diagnosis[locale]}
            </p>
          </motion.section>

          {/* ===== PROCESS (Numbered Steps) ===== */}
          <motion.section
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            className="mb-16"
          >
            <h2 className="text-3xl font-bold text-white mb-8">
              {locale === "ar" ? "خطوات العلاج" : "Treatment process"}
            </h2>
            <div className="space-y-6">
              {content.process.map((step, i) => (
                <div
                  key={i}
                  className="flex gap-6 p-6 rounded-lg bg-slate-900/40 border border-white/5"
                >
                  <div className="flex items-start">
                    <span className="flex shrink-0 items-center justify-center w-10 h-10 rounded-full bg-primary text-slate-900 font-bold">
                      {i + 1}
                    </span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-white mb-2">
                      {step.title[locale]}
                    </h3>
                    <p className="text-slate-300">{step.body[locale]}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.section>

          {/* ===== TECHNOLOGY ===== */}
          {content.technology && (
            <motion.section
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              className="mb-16"
            >
              <h2 className="text-3xl font-bold text-white mb-6">
                {locale === "ar" ? "التكنولوجيا المستخدمة" : "Technology"}
              </h2>
              {!isTrustworthy(content.technology.provenance) && (
                <UnverifiedBadge locale={locale} />
              )}
              <ul className="space-y-2 text-slate-300">
                {content.technology.value[locale].map((item, i) => (
                  <li key={i} className="flex gap-3">
                    <span className="text-primary shrink-0 me-2">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.section>
          )}

          {/* ===== MATERIALS ===== */}
          {content.materials && (
            <motion.section
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              className="mb-16"
            >
              <h2 className="text-3xl font-bold text-white mb-6">
                {locale === "ar" ? "المواد المستخدمة" : "Materials"}
              </h2>
              {!isTrustworthy(content.materials.provenance) && (
                <UnverifiedBadge locale={locale} />
              )}
              <ul className="space-y-2 text-slate-300">
                {content.materials.value[locale].map((item, i) => (
                  <li key={i} className="flex gap-3">
                    <span className="text-primary shrink-0 me-2">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.section>
          )}

          {/* ===== AFTERCARE ===== */}
          {content.aftercare && (
            <motion.section
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              className="mb-16"
            >
              <h2 className="text-3xl font-bold text-white mb-6">
                {locale === "ar" ? "الرعاية اللاحقة" : "Aftercare"}
              </h2>
              <ul className="space-y-3">
                {content.aftercare[locale].map((item, i) => (
                  <li key={i} className="flex gap-3 text-slate-300">
                    <span className="text-primary shrink-0 me-2">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.section>
          )}

          {/* ===== RISKS AND LIMITS (Prominent) ===== */}
          <motion.section
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            className="mb-16 p-8 rounded-lg bg-red-500/10 border border-red-500/20"
          >
            <h2 className="text-3xl font-bold text-white mb-6">
              {locale === "ar"
                ? "المخاطر والقيود"
                : "Risks and limitations"}
            </h2>
            <p className="text-slate-300 mb-6 italic">
              {locale === "ar"
                ? "هذا القسم مهم. نحن نعرضه بوضوح لأن المرضى يستحقون معلومات صادقة عن القيود والمخاطر."
                : "This section is important. We present it prominently because you deserve honest information about what this treatment cannot do."}
            </p>
            <ul className="space-y-4">
              {content.risksAndLimits[locale].map((item, i) => (
                <li key={i} className="flex gap-3 text-slate-300">
                  <span className="text-red-400 shrink-0 me-2">•</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </motion.section>

          {/* ===== TIMELINE ===== */}
          <motion.section
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            className="mb-16"
          >
            <h2 className="text-3xl font-bold text-white mb-6">
              {locale === "ar" ? "المدة الزمنية" : "Timeline"}
            </h2>
            <p className="text-slate-300 leading-relaxed">
              {content.timeline[locale]}
            </p>
          </motion.section>

          {/* ===== COMPARISON TABLE ===== */}
          {content.comparison && (
            <motion.section
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              className="mb-16"
            >
              <h2 className="text-3xl font-bold text-white mb-6">
                {content.comparison.heading[locale]}
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <caption className="sr-only">
                    {content.comparison.heading[locale]}
                  </caption>
                  <thead>
                    <tr className="border-b-2 border-white/10">
                      <th
                        scope="col"
                        className="text-start ps-4 py-4 text-white font-bold"
                      >
                        {locale === "ar" ? "الخيار" : "Option"}
                      </th>
                      <th
                        scope="col"
                        className="text-start ps-4 py-4 text-white font-bold"
                      >
                        {locale === "ar" ? "مناسب لـ" : "Best for"}
                      </th>
                      <th
                        scope="col"
                        className="text-start ps-4 py-4 text-white font-bold"
                      >
                        {locale === "ar" ? "الاعتبارات" : "Considerations"}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {content.comparison.rows.map((row, i) => (
                      <tr
                        key={i}
                        className="hover:bg-slate-900/30 transition-colors"
                      >
                        <td className="ps-4 py-4 text-white font-medium">
                          {row.option[locale]}
                        </td>
                        <td className="ps-4 py-4 text-slate-300">
                          {row.bestFor[locale]}
                        </td>
                        <td className="ps-4 py-4 text-slate-300">
                          {row.considerations[locale]}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.section>
          )}

          {/* ===== COST FACTORS ===== */}
          <motion.section
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            className="mb-16"
          >
            <h2 className="text-3xl font-bold text-white mb-6">
              {locale === "ar"
                ? "عوامل التكلفة"
                : "What affects the cost"}
            </h2>
            <ul className="space-y-3">
              {content.costFactors[locale].map((item, i) => (
                <li key={i} className="flex gap-3 text-slate-300">
                  <span className="text-primary shrink-0 me-2">•</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </motion.section>

          {/* ===== FAQs (Radix Accordion, HTML-native content) ===== */}
          {content.faqs.length > 0 && (
            <motion.section
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              className="mb-16"
            >
              <h2 className="text-3xl font-bold text-white mb-8">
                {locale === "ar" ? "الأسئلة الشائعة" : "Frequently asked questions"}
              </h2>
              <Accordion type="single" collapsible className="border border-white/5 rounded-lg">
                {content.faqs.map((faq, i) => (
                  <AccordionItem key={i} value={`faq-${i}`}>
                    <AccordionTrigger className="px-6 text-white hover:no-underline hover:text-primary/90">
                      {faq.question[locale]}
                    </AccordionTrigger>
                    <AccordionContent className="px-6 text-slate-300 leading-relaxed">
                      {faq.answer[locale]}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </motion.section>
          )}

          {/* ===== RELATED LINKS ===== */}
          <motion.section
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            className="mb-16"
          >
            <div className="space-y-12">
              {/* Related Services */}
              {relatedServicesList.length > 0 && (
                <div>
                  <h2 className="text-2xl font-bold text-white mb-4">
                    {locale === "ar" ? "خدمات ذات صلة" : "Related services"}
                  </h2>
                  <ul className="space-y-2">
                    {relatedServicesList.map((svc) => (
                      <li key={svc.slug}>
                        <a
                          href={`/${locale}/services/${svc.slug}`}
                          className="text-primary hover:underline"
                        >
                          {svc.name}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Related Doctors */}
              {relatedDoctorsList.length > 0 && (
                <div>
                  <h2 className="text-2xl font-bold text-white mb-4">
                    {locale === "ar" ? "الأطباء المتخصصون" : "Specialists"}
                  </h2>
                  <ul className="space-y-2">
                    {relatedDoctorsList.map((doc) => (
                      <li key={doc.slug}>
                        <a
                          href={`/${locale}/doctors/${doc.slug}`}
                          className="text-primary hover:underline"
                        >
                          {doc.name}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Related Cases */}
              {relatedCasesList.length > 0 && (
                <div>
                  <h2 className="text-2xl font-bold text-white mb-4">
                    {locale === "ar" ? "حالات سريرية" : "Clinical cases"}
                  </h2>
                  <ul className="space-y-2">
                    {relatedCasesList.map((cas) => (
                      <li key={cas.slug}>
                        <a
                          href={`/${locale}/cases/${cas.slug}`}
                          className="text-primary hover:underline"
                        >
                          {cas.name}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </motion.section>

          {/* ===== CONSULTATION CTA ===== */}
          <motion.section
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            className="mt-24 p-8 rounded-lg bg-primary/20 border border-primary/40 text-center"
          >
            <h2 className="text-2xl font-bold text-white mb-4">
              {locale === "ar"
                ? "هل لديك أسئلة؟"
                : "Ready to learn more?"}
            </h2>
            <p className="text-slate-300 mb-6">
              {locale === "ar"
                ? "احجز استشارة مع أحد متخصصينا للحصول على تقييم شامل وخطة علاج مخصصة."
                : "Book a consultation with one of our specialists for a comprehensive evaluation and personalized treatment plan."}
            </p>
            <a
              href={`/${locale}/contact`}
              className="inline-block px-8 py-3 bg-primary text-slate-900 font-bold rounded-lg hover:bg-primary/90 transition-colors"
            >
              {locale === "ar" ? "احجز استشارة" : "Book consultation"}
            </a>
          </motion.section>
        </div>
      </main>

      <QuickActionBar />
    </>
  );
}
